// WbProductGroupsPage — /weighbridge/settings/product-groups (admin + manager).
//
// Migration 169. Two sections:
//   - Global groups (platform-managed, read-only) with an Active toggle that
//     opts the tenant in/out via the exclusion list (active = not excluded).
//   - Custom groups: full CRUD on the tenant's own groups.
//
// Groups feed the WB + kiosk product pickers (one shared WbProductCombobox)
// and the product management form's group selector.

import { useState } from 'react';
import { Layers, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateProductGroup,
  useDeleteProductGroup,
  useToggleGlobalGroupExclusion,
  useUpdateProductGroup,
  useWbProductGroups,
  type WbProductGroup,
} from '../hooks/useWbProductGroups';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbProductGroupsPage.module.css';

export function WbProductGroupsPage(): JSX.Element {
  const groupsQuery = useWbProductGroups();
  const groups = groupsQuery.data?.groups ?? [];
  const globalGroups = groups.filter((g) => g.is_global);
  const customGroups = groups.filter((g) => !g.is_global);

  const createMut = useCreateProductGroup();
  const toggleMut = useToggleGlobalGroupExclusion();

  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WbProductGroup | null>(null);
  const deleteMut = useDeleteProductGroup();

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newName.trim() === '' || newCode.trim() === '') {
      setError('Name and code are required.');
      return;
    }
    createMut.mutate(
      { name: newName.trim(), code: newCode.trim() },
      {
        onSuccess: () => {
          setNewName('');
          setNewCode('');
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  function handleToggleGlobal(group: WbProductGroup) {
    setError(null);
    // Active = NOT excluded. Currently active → exclude; currently hidden → include.
    toggleMut.mutate(
      { encId: group.enc_id, exclude: !group.is_excluded },
      { onError: (err) => setError(extractErrorMessage(err)) },
    );
  }

  function handleDeleteConfirm() {
    if (deleteTarget === null) return;
    deleteMut.mutate(deleteTarget.enc_id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (err) => {
        setDeleteTarget(null);
        setError(extractErrorMessage(err));
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Product Groups"
        subtitle="Organise products into groups for easier selection in the workspace and kiosk."
      />

      {error ? <p className={styles.error}>{error}</p> : null}

      {groupsQuery.isLoading ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading groups" />
          </div>
        </Card>
      ) : (
        <>
          {/* -- Global groups ----------------------------------------- */}
          <Card title="Global groups">
            <p className={styles.sectionHelp}>
              Platform-managed groups, shared across all tenants. You cannot edit
              them, but you can hide ones you don’t use with the Active toggle.
            </p>
            {globalGroups.length === 0 ? (
              <p className={styles.muted}>No global groups available.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {globalGroups.map((g) => (
                    <tr key={g.code}>
                      <td>{g.name}</td>
                      <td className={styles.codeCell}>{g.code}</td>
                      <td>
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            checked={!g.is_excluded}
                            disabled={toggleMut.isPending}
                            onChange={() => handleToggleGlobal(g)}
                            aria-label={`Active: ${g.name}`}
                          />
                          <span>
                            {g.is_excluded ? (
                              <Badge variant="muted">Hidden</Badge>
                            ) : (
                              <Badge variant="success">Active</Badge>
                            )}
                          </span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* -- Custom groups ----------------------------------------- */}
          <Card title="Your custom groups">
            <form className={styles.createRow} onSubmit={handleCreate}>
              <input
                type="text"
                placeholder="Group name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                aria-label="New group name"
              />
              <input
                type="text"
                placeholder="CODE"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                onBlur={(e) => setNewCode(e.target.value.toUpperCase())}
                aria-label="New group code"
              />
              <Button type="submit" variant="primary" icon={Plus} loading={createMut.isPending}>
                Add group
              </Button>
            </form>

            {customGroups.length === 0 ? (
              <EmptyState
                icon={Layers}
                title="No custom groups yet"
                description="Create a group above to organise your products."
              />
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {customGroups.map((g) => (
                    <CustomGroupRow
                      key={g.code}
                      group={g}
                      onDelete={() => setDeleteTarget(g)}
                      onError={setError}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete group?"
        message={
          <p>
            Delete <strong>{deleteTarget?.name ?? 'this group'}</strong>? Products
            in it become ungrouped. This cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}

interface CustomGroupRowProps {
  group: WbProductGroup;
  onDelete: () => void;
  onError: (msg: string) => void;
}

function CustomGroupRow({ group, onDelete, onError }: CustomGroupRowProps): JSX.Element {
  const updateMut = useUpdateProductGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [code, setCode] = useState(group.code);

  function save() {
    updateMut.mutate(
      { encId: group.enc_id, body: { name: name.trim(), code: code.trim() } },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => onError(extractErrorMessage(err)),
      },
    );
  }

  function toggleActive() {
    updateMut.mutate(
      { encId: group.enc_id, body: { is_active: !group.is_active } },
      { onError: (err) => onError(extractErrorMessage(err)) },
    );
  }

  if (editing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={`Edit name: ${group.name}`}
          />
        </td>
        <td>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onBlur={(e) => setCode(e.target.value.toUpperCase())}
            aria-label={`Edit code: ${group.name}`}
          />
        </td>
        <td colSpan={2} className={styles.actionsCell}>
          <Button variant="primary" size="sm" onClick={save} loading={updateMut.isPending}>
            Save
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setName(group.name);
              setCode(group.code);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{group.name}</td>
      <td className={styles.codeCell}>{group.code}</td>
      <td>
        <button
          type="button"
          className={styles.statusBtn}
          onClick={toggleActive}
          disabled={updateMut.isPending}
          aria-label={`Toggle active: ${group.name}`}
        >
          {group.is_active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Inactive</Badge>
          )}
        </button>
      </td>
      <td className={styles.actionsCell}>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="ghost" size="sm" icon={Trash2} onClick={onDelete}>
          Delete
        </Button>
      </td>
    </tr>
  );
}
